import { DataTableParamState } from "@/common/hooks/useDataTableParamState";
import { UsersTable } from "@/company/components/UsersTable";
import { useUsersData } from "@/company/data/useUsersData";
import type { SegmentAudienceCountState } from "@/company/hooks/useSegmentAudienceCount";
import { UIFilter } from "@reflag/shared/filter";
import { SegmentDTO } from "@reflag/shared/segmentAPI";
import { UsersSortByColumn } from "@reflag/shared/userAPI";

type Props = {
  segment: SegmentDTO;
  audienceCount: SegmentAudienceCountState;
  tableState: DataTableParamState<UsersSortByColumn, UsersSortByColumn>;
  filter: UIFilter;
  onFilterChange: (filter: UIFilter | undefined) => void;
  toolbarRightActions?: React.ReactNode;
};

export const UsersTableWithData = ({
  segment,
  audienceCount,
  tableState,
  filter,
  onFilterChange,
  toolbarRightActions,
}: Props) => {
  const { searchQuery, sorting, pageIndex, columnStates } = tableState;

  // We only handle sorting one column for now
  const { id: sortBy, desc: sortDesc } = sorting[0] ?? {};

  const {
    data,
    isLoading,
    isFetching,
    isError: isListError,
    refetch: refetchList,
  } = useUsersData(
    {
      sortBy,
      sortOrder:
        sortDesc === true ? "desc" : sortDesc === false ? "asc" : undefined,
      pageIndex,
      filter,
      idNameEmailFilter: searchQuery,
      columns: columnStates.map((state) => state.id),
    },
    true, // Always enabled for users table
  );
  const { displayedTotalCount } = audienceCount;
  const isTableFetching = isFetching;
  const isError = isListError || audienceCount.status === "error";
  const refetch = () => {
    void refetchList();
    audienceCount.refetch();
  };

  return (
    <UsersTable
      data={data}
      filter={filter}
      isError={isError}
      isFetching={isTableFetching}
      isLoading={isLoading}
      segment={segment}
      tableState={tableState}
      totalCount={displayedTotalCount}
      toolbarRightActions={toolbarRightActions}
      onFilterChange={onFilterChange}
      onRetry={refetch}
    />
  );
};
